<template>
  <div class="mining-page">
    <div class="page-inner">
      <header class="page-header">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <span class="user-badge">{{ userBadge }}</span>
      </header>

      <!-- 余额 + 绑定钱包 -->
      <section class="card balance-wallet-card">
        <div class="balance-panel">
          <p class="balance-label">当前平台余额 (USDT)</p>
          <p class="balance-value">{{ formattedBalance }}</p>
          <p class="balance-rate">当前日收益率: {{ currentRateLabel }}</p>
          <p class="balance-tier-tip">{{ tierTip }}</p>
        </div>
        <div class="wallet-panel">
          <h2 class="wallet-title">绑定钱包</h2>
          <div class="wallet-address-box">
            <span class="wallet-address">{{ walletAddress || '请先连接或填写地址' }}</span>
          </div>
          <div class="wallet-btns">
            <button type="button" class="btn-yellow" @click="connectTron">连接 TRON 钱包</button>
            <button type="button" class="btn-black" @click="openBindModal">手动填写地址</button>
          </div>
          <p class="wallet-hint">无需登录，绑定钱包地址即作为用户身份</p>
        </div>
      </section>

      <!-- 矿机方案 -->
      <section class="card section-card">
        <h2 class="section-title">矿机/理财方案</h2>
        <div class="plans-grid">
          <div v-for="plan in plans" :key="plan.id" class="plan-card">
            <h3 class="plan-name">{{ plan.name }}</h3>
            <div class="plan-row">
              <span>{{ plan.rateLabel }}</span>
              <span class="plan-val">{{ plan.rate }}</span>
            </div>
            <div class="plan-row">
              <span>最小投入</span>
              <span class="plan-val">{{ plan.min }}</span>
            </div>
            <button type="button" class="btn-black plan-btn" @click="onJoinPlan(plan)">立即参与</button>
          </div>
        </div>
        <p class="section-foot">方案与收益率可按需替换。</p>
      </section>

      <!-- 收益试算 -->
      <section class="card section-card">
        <h2 class="section-title">收益试算</h2>
        <div class="calc-row">
          <input v-model="calcAmount" type="text" class="calc-input" placeholder="投入金额" />
          <input v-model="calcDays" type="text" class="calc-input" placeholder="天数" />
          <button type="button" class="btn-yellow calc-btn" @click="runCalc">计算</button>
        </div>
        <p class="calc-result">{{ calcResultText }}</p>
      </section>

      <!-- 邀请推广 -->
      <section class="card section-card">
        <h2 class="section-title">邀请与推广</h2>
        <div class="rules-block">
          <h3 class="rules-subtitle">复投算法说明</h3>
          <ul class="rules-list">
            <li>日利率档位：余额 &lt; 2,000,000 → 3.80%；2,000,000–4,999,999 → 6.80%；≥ 5,000,000 → 8.80%。</li>
            <li>复利计算：第 n 天本金 = 上一日本金 × (1 + 日利率)。收益试算等价于：投入金额 × (1 + rate)^天数。</li>
          </ul>
        </div>
        <div class="rules-block">
          <h3 class="rules-subtitle">推广邀请规则</h3>
          <ul class="rules-list">
            <li>专属链接格式：当前页面 URL + ?id=trcXXXXXXXXX（9 位数字）。系统自动生成并保存在本地。</li>
            <li>将专属链接分享给好友，好友通过你的链接访问并参与，可用于活动统计与返佣发放。</li>
            <li class="invite-line">
              <span>示例：{{ inviteLink }}</span>
              <button type="button" class="btn-black btn-copy" @click="copyInvite">复制</button>
            </li>
          </ul>
        </div>
      </section>
    </div>

    <!-- 图1：未检测到钱包 -->
    <div v-if="showWalletTip" class="modal-mask" @click.self="showWalletTip = false">
      <div class="mining-modal">
        <div class="modal-head">提示</div>
        <div class="modal-body modal-body-center">
          <p>未检测到 TRON 钱包或未授权，请改用「手动填写地址」。</p>
        </div>
        <button type="button" class="modal-btn-full" @click="showWalletTip = false">确定</button>
      </div>
    </div>

    <!-- 图2：绑定地址 -->
    <div v-if="showBindModal" class="modal-mask" @click.self="closeBindModal">
      <div class="mining-modal">
        <div class="modal-head">绑定地址</div>
        <div class="modal-body">
          <input
            v-model="bindAddressInput"
            class="modal-input"
            placeholder="请输入您的 TRC20 收款地址（以 T 开头，34 位）"
          />
        </div>
        <div class="modal-actions">
          <button type="button" class="modal-btn-gray" @click="closeBindModal">取消</button>
          <button type="button" class="modal-btn-yellow" @click="confirmBind">确定</button>
        </div>
      </div>
    </div>

    <!-- 未绑定钱包提示 -->
    <div v-if="showWalletRequired" class="modal-mask" @click.self="closeWalletRequired">
      <div class="mining-modal">
        <div class="modal-head">提示</div>
        <div class="modal-body modal-body-center">
          <p>请先手动填写 TRON 链钱包地址（T 开头，34 位）后再支付。</p>
        </div>
        <button type="button" class="modal-btn-full" @click="onWalletRequiredConfirm">确定</button>
      </div>
    </div>

    <!-- 图3：充值 -->
    <div v-if="showRechargeModal" class="modal-mask" @click.self="closeRechargeModal">
      <div class="mining-modal">
        <div class="modal-head">充值</div>
        <div class="modal-body">
          <input v-model="rechargeAmount" class="modal-input" type="text" />
        </div>
        <div class="modal-actions">
          <button type="button" class="modal-btn-gray" @click="closeRechargeModal">取消</button>
          <button type="button" class="modal-btn-yellow modal-btn-wide" @click="goPay">去支付</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { message } from 'ant-design-vue';
import { addOrder } from '../util/api';
import { initSpecialProductPage, resolveLinkKey } from '../util/specialProduct';
import { readReferralFromSearch, attachGameGroupId } from '../util/deeplink';

const STORAGE_WALLET = 'wakuang_wallet';
const STORAGE_REF_ID = 'wakuang_ref_id';
const PLATFORM_BALANCE = 7832700;

const TIERS = [
  { min: 0, max: 1999999, rate: 0.038, label: '3.80%', next: '2,000,000 USDT 可提升至 6.80%' },
  { min: 2000000, max: 4999999, rate: 0.068, label: '6.80%', next: '5,000,000 USDT 可提升至 8.80%' },
  { min: 5000000, max: Infinity, rate: 0.088, label: '8.80%', next: '已达最高档' },
];

const PLANS = [
  { id: 'basic', name: '基础计划', rateLabel: '日收益', rate: '3.00%', min: '100 USDT', minAmount: 100, dailyRate: 0.03 },
  { id: 'advanced', name: '进阶计划', rateLabel: '当前收益', rate: '6.80%', min: '500 USDT', minAmount: 500, dailyRate: 0.068 },
  { id: 'max', name: '上限计划', rateLabel: '最高日收益', rate: '8.80%', min: '2000 USDT', minAmount: 2000, dailyRate: 0.088 },
];

function isValidTronAddress(address) {
  return /^T[a-zA-Z0-9]{33}$/.test((address || '').trim());
}

function randomOrderId() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let s = '';
  for (let i = 0; i < 16; i += 1) {
    s += chars[Math.floor(Math.random() * chars.length)];
  }
  return s;
}

function randomRefId() {
  const n = Math.floor(100000000 + Math.random() * 900000000);
  return `trc${n}`;
}

function getTier(balance) {
  return TIERS.find((t) => balance >= t.min && balance <= t.max) || TIERS[0];
}

export default {
  name: 'MiningEarnings',
  data() {
    return {
      platformBalance: PLATFORM_BALANCE,
      walletAddress: '',
      refId: '',
      fisherReferralCode: '',
      gameGroupId: '',
      plans: PLANS,
      calcAmount: '100',
      calcDays: '30',
      calcResultText:
        '投入 100 USDT，30 天预计收益约 242.73 USDT (按日利率 3.00% 复投演示)',
      calcRate: 0.03,
      showWalletTip: false,
      showWalletRequired: false,
      showBindModal: false,
      bindAddressInput: '',
      showRechargeModal: false,
      rechargeAmount: '100',
      selectedPlan: null,
      pendingGoPay: false,
      pageTitle: '挖矿收益',
      specialProduct: null,
    };
  },
  computed: {
    formattedBalance() {
      return this.platformBalance.toLocaleString('en-US');
    },
    currentTier() {
      return getTier(this.platformBalance);
    },
    currentRateLabel() {
      return this.currentTier.label;
    },
    tierTip() {
      const t = this.currentTier;
      if (t.next === '已达最高档') {
        return '下一档提升至 已达最高档，当平台余额达 USDT';
      }
      return `下一档提升至 ${t.next}`;
    },
    userBadge() {
      if (this.walletAddress && this.walletAddress.length >= 6) {
        return `@${this.walletAddress.slice(-6)}`;
      }
      if (this.refId) {
        return `@${this.refId}`;
      }
      return '@Q_acca';
    },
    inviteLink() {
      const base = `${window.location.origin}${window.location.pathname}`;
      return `${base}?id=${this.refId}`;
    },
  },
  async mounted() {
    const urlCtx = readReferralFromSearch(window.location.search);
    if (urlCtx.referralCode) {
      this.fisherReferralCode = urlCtx.referralCode;
    }
    this.gameGroupId = urlCtx.gameGroupId;
    this.initRefId();
    this.loadWallet();
    this.runCalc();
    const key = resolveLinkKey(this.$route) || 'mining';
    await initSpecialProductPage(this, key, {
      titleField: 'pageTitle',
      fallback: { title: '挖矿收益' },
    });
  },
  methods: {
    initRefId() {
      const fromUrl = new URLSearchParams(window.location.search).get('id');
      if (fromUrl && /^trc\d{9}$/.test(fromUrl)) {
        this.refId = fromUrl;
        localStorage.setItem(STORAGE_REF_ID, fromUrl);
        return;
      }
      const saved = localStorage.getItem(STORAGE_REF_ID);
      if (saved && /^trc\d{9}$/.test(saved)) {
        this.refId = saved;
      } else {
        this.refId = randomRefId();
        localStorage.setItem(STORAGE_REF_ID, this.refId);
      }
      if (!fromUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('id', this.refId);
        window.history.replaceState({}, '', url.toString());
      }
    },
    loadWallet() {
      const saved = localStorage.getItem(STORAGE_WALLET);
      if (saved) {
        this.walletAddress = saved;
      }
    },
    persistWallet(addr) {
      this.walletAddress = addr;
      localStorage.setItem(STORAGE_WALLET, addr);
    },
    connectTron() {
      const tw = window.tronWeb;
      if (tw && tw.ready && tw.defaultAddress?.base58) {
        const addr = tw.defaultAddress.base58;
        if (isValidTronAddress(addr)) {
          this.persistWallet(addr);
          return;
        }
      }
      this.showWalletTip = true;
    },
    openBindModal() {
      this.bindAddressInput = this.walletAddress || '';
      this.showBindModal = true;
    },
    closeBindModal() {
      this.showBindModal = false;
    },
    confirmBind() {
      const addr = (this.bindAddressInput || '').trim();
      if (!isValidTronAddress(addr)) {
        message.warning('请输入有效的 TRON 链地址（T 开头，34 位）');
        return;
      }
      this.persistWallet(addr);
      this.showBindModal = false;
      message.success('地址已绑定');
      if (this.pendingGoPay) {
        this.pendingGoPay = false;
        this.showRechargeModal = true;
      }
    },
    closeWalletRequired() {
      this.showWalletRequired = false;
      this.pendingGoPay = false;
    },
    onWalletRequiredConfirm() {
      this.showWalletRequired = false;
      this.openBindModal();
    },
    runCalc() {
      const amount = parseFloat(String(this.calcAmount).replace(/,/g, ''));
      const days = parseInt(String(this.calcDays).replace(/\D/g, ''), 10);
      if (!amount || amount <= 0 || !days || days <= 0) {
        message.warning('请输入有效的投入金额与天数');
        return;
      }
      const rate = this.calcRate;
      const total = amount * Math.pow(1 + rate, days);
      const profit = total - amount;
      const ratePct = (rate * 100).toFixed(2);
      this.calcResultText = `投入 ${amount} USDT，${days} 天预计收益约 ${profit.toFixed(2)} USDT (按日利率 ${ratePct}% 复投演示)`;
    },
    onJoinPlan(plan) {
      this.selectedPlan = plan;
      this.calcRate = plan.dailyRate;
      this.rechargeAmount = String(plan.minAmount);
      this.showRechargeModal = true;
    },
    closeRechargeModal() {
      this.showRechargeModal = false;
      this.selectedPlan = null;
    },
    async goPay() {
      const amount = parseFloat(String(this.rechargeAmount).replace(/,/g, ''));
      if (!amount || amount <= 0) {
        message.warning('请输入有效的充值金额');
        return;
      }
      const addr = (this.walletAddress || '').trim();
      if (!isValidTronAddress(addr)) {
        this.showRechargeModal = false;
        this.pendingGoPay = true;
        this.showWalletRequired = true;
        return;
      }

      const plan = this.selectedPlan;
      const payload = attachGameGroupId({
        title: plan ? plan.name : '充值',
        price: amount,
        inputNumber: 1,
        referral_code: this.fisherReferralCode || this.refId,
        orderNo: randomOrderId(),
        walletAddress: addr,
        inputGT: addr,
        sendWay: '自动发货',
        count: 999,
        sell: 999,
        src: '',
      }, this.gameGroupId);

      this.showRechargeModal = false;
      this.pendingGoPay = false;
      await addOrder(payload);
      this.$router.push({
        name: 'MiningPayment',
        params: { data: JSON.stringify(payload) },
      });
    },
    async copyInvite() {
      try {
        await navigator.clipboard.writeText(this.inviteLink);
        message.success('链接已复制');
      } catch {
        message.error('复制失败');
      }
    },
  },
};
</script>

<style scoped lang="less">
@bg: #f0f0f0;
@yellow: #f5c518;
@yellow-hover: #e6b800;
@dark: #2d2d2d;
@blue-badge: #1890ff;
@text: #222;
@muted: #888;

.mining-page {
  min-height: 100vh;
  background: @bg;
  padding: 24px 16px 48px;
}

.page-inner {
  max-width: 1100px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: @text;
}

.user-badge {
  padding: 6px 16px;
  background: @blue-badge;
  color: #fff;
  border-radius: 20px;
  font-size: 14px;
}

.card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

/* 余额 + 钱包 */
.balance-wallet-card {
  display: flex;
  gap: 0;
  padding: 0;
  overflow: hidden;
}

.balance-panel {
  flex: 1;
  background: @dark;
  color: @yellow;
  padding: 28px 32px;
  min-width: 0;
}

.balance-label {
  margin: 0 0 8px;
  font-size: 15px;
  color: @yellow;
}

.balance-value {
  margin: 0 0 16px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.02em;
}

.balance-rate {
  margin: 0 0 8px;
  font-size: 15px;
}

.balance-tier-tip {
  margin: 0;
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.5;
}

.wallet-panel {
  flex: 1;
  padding: 28px 32px;
  min-width: 280px;
}

.wallet-title {
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 700;
  color: @text;
}

.wallet-address-box {
  background: #e8f4fc;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  min-height: 48px;
}

.wallet-address {
  font-size: 14px;
  color: #333;
  word-break: break-all;
  line-height: 1.5;
}

.wallet-btns {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn-yellow {
  padding: 10px 20px;
  background: @yellow;
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: @yellow-hover;
  }
}

.btn-black {
  padding: 10px 20px;
  background: #1a1a1a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #333;
  }
}

.wallet-hint {
  margin: 14px 0 0;
  font-size: 13px;
  color: @muted;
}

/* 通用区块 */
.section-card {
  padding: 24px 28px;
}

.section-title {
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: 700;
  color: @text;
}

.section-foot {
  margin: 16px 0 0;
  font-size: 13px;
  color: @muted;
}

/* 方案 */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.plan-card {
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
}

.plan-name {
  margin: 0 0 16px;
  font-size: 17px;
  font-weight: 700;
}

.plan-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 14px;
  color: #444;
}

.plan-val {
  font-weight: 600;
  color: @text;
}

.plan-btn {
  width: 100%;
  margin-top: 12px;
  padding: 10px;
}

/* 试算 */
.calc-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.calc-input {
  width: 120px;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  outline: none;

  &:focus {
    border-color: @blue-badge;
  }
}

.calc-btn {
  min-width: 88px;
  height: 44px;
}

.calc-result {
  margin: 16px 0 0;
  font-size: 14px;
  color: @muted;
  line-height: 1.6;
}

/* 规则 */
.rules-block {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.rules-subtitle {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: #444;
}

.rules-list {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  color: @muted;
  line-height: 1.75;

  li {
    margin-bottom: 8px;
  }
}

.invite-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  list-style: none;
  margin-left: -18px;
  padding-left: 0;

  span {
    word-break: break-all;
    flex: 1;
    min-width: 200px;
  }
}

.btn-copy {
  flex-shrink: 0;
  padding: 6px 14px;
  font-size: 13px;
}

/* 移动端 */
@media (max-width: 768px) {
  .mining-page {
    padding: 16px 12px 32px;
  }

  .page-title {
    font-size: 22px;
  }

  .user-badge {
    font-size: 12px;
    padding: 5px 12px;
  }

  .balance-wallet-card {
    flex-direction: column;
  }

  .balance-panel {
    padding: 22px 20px;
  }

  .balance-value {
    font-size: 30px;
  }

  .wallet-panel {
    padding: 22px 20px;
    min-width: 0;
  }

  .wallet-btns {
    flex-direction: column;
  }

  .wallet-btns .btn-yellow,
  .wallet-btns .btn-black {
    width: 100%;
    text-align: center;
  }

  .section-card {
    padding: 20px 16px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .calc-row {
    flex-direction: column;
    align-items: stretch;
  }

  .calc-input {
    width: 100%;
  }

  .calc-btn {
    width: 100%;
  }
}

/* 弹窗 */
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.mining-modal {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.modal-head {
  background: @yellow;
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  padding: 14px 16px;
}

.modal-body {
  padding: 24px 20px;

  p {
    margin: 0;
    font-size: 15px;
    color: #222;
    line-height: 1.6;
  }
}

.modal-body-center {
  text-align: center;
}

.modal-input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: @yellow;
  }

  &::placeholder {
    color: #bbb;
    font-size: 13px;
  }
}

.modal-btn-full {
  display: block;
  width: calc(100% - 40px);
  margin: 0 auto 24px;
  padding: 12px;
  background: @yellow;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 0 20px 24px;
}

.modal-btn-gray {
  flex: 0 0 32%;
  padding: 12px;
  background: #e8e8e8;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  color: #222;
  cursor: pointer;
}

.modal-btn-yellow {
  flex: 1;
  padding: 12px;
  background: @yellow;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
}

.modal-btn-wide {
  flex: 1.8;
}
</style>
